import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Flame, Trophy, Zap, BookOpen } from "lucide-react";
import { MODULES, computeLevel, BADGES } from "../data/modules";
import { useProgress } from "../context/ProgressContext";
import { PassportBadge } from "../components/PassportBadge";

function moduleProgress(m, completed) {
  const total = m.lessons.length;
  const done = m.lessons.filter((l) => completed.includes(l.id)).length;
  return { total, done, pct: Math.round((done / total) * 100) };
}

export default function Home() {
  const { progress } = useProgress();
  const { level, into, pct, needed } = computeLevel(progress.xp || 0);

  const earnedBadges = progress.badges || [];
  const recentBadge = [...BADGES].reverse().find((b) => earnedBadges.includes(b.id));

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
      {/* HERO */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="md:col-span-8 relative border border-border bg-surface p-8 md:p-12 overflow-hidden grain"
          data-testid="hero-card"
        >
          <div className="font-mono-code text-[10px] uppercase tracking-widest text-primary mb-4">
            prompt · engineering · micro-course
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] max-w-2xl">
            Learn to <em className="text-primary not-italic">talk</em> to machines,{" "}
            <span className="italic">beautifully.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
            Five bite-sized modules. Real scenarios. An AI coach that scores every prompt you
            write. Ten minutes a day is all it takes.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 items-center">
            <Link
              to={`/module/${MODULES[0].id}`}
              data-testid="hero-start-btn"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 font-medium border-2 border-primary hard-shadow-ink"
            >
              Start Module 01 <ArrowUpRight className="w-4 h-4" />
            </Link>
            <div className="font-mono-code text-xs text-muted-foreground uppercase tracking-widest">
              5 modules · ~45 min total
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-1 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="border border-border bg-background p-6 md:p-8"
            data-testid="stat-level"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">Level</div>
                <div className="font-serif-display text-5xl md:text-6xl mt-1">{level}</div>
              </div>
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <div className="mt-4">
              <div className="h-1 bg-border relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-2 font-mono-code text-[10px] text-muted-foreground">
                {into}/{needed} XP TO LEVEL {level + 1}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="border border-border bg-background p-6 md:p-8 grid grid-cols-2 gap-4"
          >
            <div data-testid="stat-xp">
              <div className="flex items-center gap-1.5 mb-1">
                <Zap className="w-3.5 h-3.5 text-primary" />
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">XP</div>
              </div>
              <div className="font-serif-display text-3xl md:text-4xl">{progress.xp || 0}</div>
            </div>
            <div data-testid="stat-streak">
              <div className="flex items-center gap-1.5 mb-1">
                <Flame className="w-3.5 h-3.5 text-primary" />
                <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">Streak</div>
              </div>
              <div className="font-serif-display text-3xl md:text-4xl">{progress.streak || 0}</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* MODULES */}
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="font-serif-display text-3xl md:text-4xl">The Five Modules</h2>
        <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">
          curriculum · v1
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="modules-grid">
        {MODULES.map((m, i) => {
          const { done, total, pct } = moduleProgress(m, progress.completed_lessons || []);
          const span = i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : i === 3 ? "md:col-span-4" : "md:col-span-8";
          const complete = done === total;
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i, duration: 0.4 }}
              className={span}
            >
              <Link
                to={`/module/${m.id}`}
                data-testid={`module-card-${m.id}`}
                className="group relative block border border-border bg-surface hover:border-primary transition-colors overflow-hidden h-full"
              >
                <div
                  className="aspect-[16/9] md:aspect-[16/8] w-full bg-cover bg-center relative"
                  style={{ backgroundImage: `url(${m.cover})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute top-4 left-4 font-mono-code text-[11px] text-white/90 uppercase tracking-widest">
                    module {m.number}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="font-serif-display text-white text-3xl md:text-4xl leading-none">
                      {m.title}
                    </div>
                    <div className="font-mono-code text-[11px] text-white/80 uppercase tracking-widest mt-1">
                      {m.subtitle}
                    </div>
                  </div>
                  {complete && (
                    <div className="absolute top-4 right-4 px-2 py-1 bg-success text-success-foreground font-mono-code text-[10px] uppercase tracking-widest">
                      ✓ Complete
                    </div>
                  )}
                </div>
                <div className="p-6 md:p-8">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">
                      {m.duration} · {total} lessons
                    </div>
                    <div className="font-mono-code text-xs">
                      {done}/{total}
                    </div>
                  </div>
                  <div className="h-1 bg-border relative overflow-hidden mb-4">
                    <div className="absolute inset-y-0 left-0 bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <BookOpen className="w-4 h-4" />
                    <span className="text-sm">
                      {done === 0 ? "Start module" : complete ? "Review" : "Continue"}
                    </span>
                    <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* BADGES */}
      <div className="mt-16 grid md:grid-cols-12 gap-6">
        <div className="md:col-span-8 border border-border p-8 md:p-10 bg-background">
          <div className="flex items-baseline justify-between mb-6">
            <h3 className="font-serif-display text-2xl md:text-3xl">Passport Stamps</h3>
            <Link to="/badges" data-testid="badges-see-all" className="font-mono-code text-[10px] uppercase tracking-widest text-primary hover:underline">
              see all →
            </Link>
          </div>
          <div className="flex flex-wrap gap-8">
            {BADGES.slice(0, 6).map((b) => (
              <PassportBadge
                key={b.id}
                label={b.label}
                sub="PROMPT CRAFT"
                earned={earnedBadges.includes(b.id)}
                testId={`badge-preview-${b.id}`}
              />
            ))}
          </div>
        </div>

        <div className="md:col-span-4 border border-border p-8 md:p-10 bg-surface flex flex-col justify-between">
          <div>
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-muted-foreground">
              latest achievement
            </div>
            <div className="font-serif-display text-3xl md:text-4xl mt-1 leading-tight">
              {recentBadge ? recentBadge.label : "No badge yet"}
            </div>
            <div className="text-sm text-muted-foreground mt-3 leading-relaxed">
              {recentBadge ? recentBadge.desc : "Complete a lesson to earn your first stamp."}
            </div>
          </div>
          <Link
            to="/module/m1"
            data-testid="cta-jump-in"
            className="mt-8 inline-flex items-center gap-2 border-2 border-primary text-primary px-4 py-2.5 font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Jump in <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
